import type { PresenterRef } from "./types";

/** Random id with a readable prefix, e.g. `ses-k3j9x2`. */
export function createId(prefix: string): string {
  const rand = Math.random().toString(36).slice(2, 8);
  const time = Date.now().toString(36).slice(-4);
  return `${prefix}-${time}${rand}`;
}

/**
 * Mock-only password hash (FNV-1a). Real authentication moves to Supabase Auth;
 * this only keeps plain passwords out of localStorage during the prototype phase.
 */
export function mockHash(password: string): string {
  let hash = 0x811c9dc5;
  const input = `love-cpmd::${password}`;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
}

/** Encode a presenter as a string for activity payloads: `member:m01`, `guest:gp1`, `free:Name`. */
export function encodeRef(ref: PresenterRef): string {
  return ref.kind === "free" ? `free:${ref.name}` : `${ref.kind}:${ref.id}`;
}

export function decodeRef(value: string | undefined | null): PresenterRef | undefined {
  if (!value) return undefined;
  const idx = value.indexOf(":");
  const kind = value.slice(0, idx);
  const rest = value.slice(idx + 1);
  if (kind === "member" || kind === "guest") return { kind, id: rest };
  if (kind === "free") return { kind, name: rest };
  return undefined;
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const skip = new Set(["muhammad", "siti", "m."]);
  const meaningful = parts.length > 2 && skip.has(parts[0].toLowerCase()) ? parts.slice(1) : parts;
  const first = meaningful[0]?.[0] ?? "";
  const second = meaningful.length > 1 ? meaningful[meaningful.length - 1][0] : "";
  return (first + second).toUpperCase();
}

export function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
